/**
 * UserDetailDrawer component
 * Sheet-based drawer showing full user details with editing capabilities
 */
import { useState, useEffect } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useUserDetail, useUpdateUserRole } from '@/hooks/useUsers'
import { RoleBadge } from './RoleBadge'
import { RoleSelector } from './RoleSelector'
import { UserStatusToggle } from './UserStatusToggle'
import { RoleHistoryTimeline } from './RoleHistoryTimeline'
import { RoleChangeDialog } from './RoleChangeDialog'
import type { RoleCode } from '../../types/admin'

export interface UserDetailDrawerProps {
  /** Whether the drawer is open */
  open: boolean
  /** Called when open state changes */
  onOpenChange: (open: boolean) => void
  /** ID of the user to display */
  userId: string
}

/**
 * Sheet-based drawer for viewing and editing user details
 */
export function UserDetailDrawer({
  open,
  onOpenChange,
  userId,
}: UserDetailDrawerProps) {
  const { data: user, isLoading, error } = useUserDetail(userId)
  const updateRole = useUpdateUserRole()

  // Local state for editing
  const [selectedRole, setSelectedRole] = useState<RoleCode | undefined>()
  const [isActive, setIsActive] = useState<boolean>(true)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  // Sync local state with fetched user data
  useEffect(() => {
    if (user) {
      setSelectedRole(user.role)
      setIsActive(user.isActive)
    }
  }, [user])

  // Determine if there are unsaved changes
  const hasChanges = user && (
    selectedRole !== user.role ||
    isActive !== user.isActive
  )

  const handleSave = () => {
    if (selectedRole && selectedRole !== user?.role) {
      setShowConfirmDialog(true)
    }
    // TODO: Handle isActive change when API is available
  }

  const handleConfirmRoleChange = (reason: string) => {
    if (user && selectedRole) {
      updateRole.mutate(
        { oid: user.id, role: selectedRole, reason: reason || undefined },
        {
          onSuccess: () => {
            setShowConfirmDialog(false)
            // Keep drawer open to show updated data
          },
        }
      )
    }
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
      timeZoneName: 'short',
    })
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>User Details</SheetTitle>
            <SheetDescription>
              View and edit user information
            </SheetDescription>
          </SheetHeader>

          {isLoading && (
            <div className="space-y-4 py-4" data-testid="user-detail-skeleton">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          )}

          {error && (
            <div className="py-8 text-center text-muted-foreground">
              <p>Failed to load user details.</p>
              <p className="text-sm">Please try again later.</p>
            </div>
          )}

          {user && !isLoading && !error && (
            <div className="space-y-6 py-4">
              {/* User Header */}
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-medium">{user.displayName}</h3>
                  <RoleBadge role={user.role} size="sm" />
                </div>
                <p className="text-sm text-muted-foreground">{user.email}</p>
              </div>

              {/* Role Section */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Role</label>
                <RoleSelector
                  value={selectedRole || user.role}
                  onChange={setSelectedRole}
                />
              </div>

              {/* Status Section */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Status</label>
                <UserStatusToggle
                  isActive={isActive}
                  onChange={setIsActive}
                />
              </div>

              {/* Account Information */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Account Information</h4>
                <dl className="grid grid-cols-2 gap-2 text-sm">
                  <dt className="text-muted-foreground">Created:</dt>
                  <dd>{formatDate(user.createdAt)}</dd>
                  <dt className="text-muted-foreground">Last Login:</dt>
                  <dd>{formatDate(user.lastLoginAt)}</dd>
                  <dt className="text-muted-foreground">Last Updated:</dt>
                  <dd>{formatDate(user.updatedAt)}</dd>
                </dl>
              </div>

              {/* Role History */}
              <RoleHistoryTimeline history={user.roleHistory} />
            </div>
          )}

          <SheetFooter className="mt-4">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges || updateRole.isPending}
            >
              {updateRole.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Role Change Confirmation Dialog */}
      {user && selectedRole && (
        <RoleChangeDialog
          open={showConfirmDialog}
          onOpenChange={setShowConfirmDialog}
          userName={user.displayName}
          userEmail={user.email}
          currentRole={user.role}
          newRole={selectedRole}
          onConfirm={handleConfirmRoleChange}
          isPending={updateRole.isPending}
        />
      )}
    </>
  )
}
